import { StimulusDropdown } from "@/dropdown";
import { SelectControl } from "@/form";
import utils from "@/utils";
import { AbstractFormControl } from "@/form";

export declare type TreeNodeData = [
        /** id */ string,
        /** name */ string,
        /** parentId */ string?
];

export class TreeNode {
    public constructor(
        public id: string,
        public name: string,
        public parent?: TreeNode,
        public children: TreeNode[] = []
    ) { }
}

/** mod function that always returns a positive value */
export function mod(n: number, m: number) {
    return ((n % m) + m) % m;
}

export class Tree {

    public static initialize(dataLists: TreeNodeData[][]) {
        let branches = this.resolveBranches(dataLists);
        let topLevelNodes = branches[0];
        let leaves = branches[branches.length - 1];
        return new this(topLevelNodes, leaves, branches.length);
    }

    /** Links all of the parents + children together */
    private static resolveBranches(dataLists: TreeNodeData[][]) {
        let branches: TreeNode[][] = [];
        for (let depth = 0; depth < dataLists.length; depth++) {
            let nodes = [];
            for (let [id, name, parentId] of dataLists[depth]) {
                let node = new TreeNode(id, name);
                if (depth > 0) {
                    let parent = branches[depth - 1].find(node => node.id == parentId);
                    node.parent = parent;
                    parent.children.push(node);
                }
                nodes.push(node);
            }
            branches.push(nodes);
        }
        return branches;
    }

    public constructor(
        public nodes: TreeNode[],
        private leaves: TreeNode[],
        public readonly depth: number,
    ) { }

    getLeaf(leafId: string): TreeNode {
        return this.leaves.find(leaf => leaf.id == leafId);
    }

    getNeighbor(node: TreeNode, distance: number): TreeNode {
        if (distance == 0) {
            return node;
        } else if (node.parent) {
            return this.getNeighborBelowRoot(node, node.parent, distance);
        } else {
            return this.getNeighborAtRoot(node, distance);
        }
    }

    private getNeighborAtRoot(node: TreeNode, distance: number): TreeNode {
        let siblings = this.nodes;
        let index = siblings.indexOf(node);
        let next = mod(index + distance, siblings.length);
        return siblings[next];
    }

    private getNeighborBelowRoot(node: TreeNode, parent: TreeNode, distance: number): TreeNode {
        let siblings = parent.children;
        let index = siblings.indexOf(node);
        let next = index + distance;
        if (next >= siblings.length) {
            return this.getNextCousin(parent, next - siblings.length + 1);
        } else if (next < 0) {
            return this.getPreviousCousin(parent, next);
        }
        return siblings[next];
    }

    private getPreviousCousin(parent: TreeNode, distance: number) {
        let cousins = this.getNeighbor(parent, -1).children;
        let previous = cousins[cousins.length - 1];
        return this.getNeighbor(previous, distance + 1);
    }

    private getNextCousin(parent: TreeNode, distance: number) {
        let cousins = this.getNeighbor(parent, 1).children;
        let next = cousins[0];
        return this.getNeighbor(next, distance - 1);
    }
}

export class TreeList {

    public static create() {
        let ul = document.createElement('ul');
        ul.classList.add('ww-tree-list');
        return new TreeList(ul);
    }

    public selectedItemId: string;

    public constructor(
        public readonly el: HTMLUListElement,
    ) { }

    public getItem(itemId: string) {
        return this.el.querySelector(`[data-id='${itemId}']`);
    }

    public setSelectedItemId(itemId?: string) {
        if (this.selectedItemId) {
            this.getItem(this.selectedItemId).classList.remove(
                'ww-dropdown__option--selected',
                'accented_background',
                'accented_text'
            );
        }

        this.selectedItemId = itemId;
        if (this.selectedItemId) {
            this.getItem(this.selectedItemId).classList.add(
                'ww-dropdown__option--selected',
                'accented_background',
                'accented_text'
            );
        }
    }

    public clear() {
        this.selectedItemId = null;
        this.el.innerHTML = '';
    }

    public addItem(node: TreeNode) {
        let item = this.newItem(node);
        this.el.appendChild(item);
        return item;
    }

    private newItem(node: TreeNode) {
        let li = document.createElement('li');
        li.classList.add('ww-dropdown__option');
        li.dataset.id = node.id.toString();
        li.innerHTML = node.name;
        return li;
    }
}

class TreeSelectButton {
    constructor(protected el?: HTMLButtonElement) { }

    onClick(callback: () => void) {
        if (this.el) this.el.addEventListener('click', callback);
    }
}

class ClearButton extends TreeSelectButton {

    show() {
        if (this.el) this.el.classList.remove('ww-view--hidden');
    }

    hide() {
        if (this.el) this.el.classList.add('ww-view--hidden');
    }
}

export interface TreeSelectData {
    value: string | null;
    separator: string | null;
    branches: TreeNodeData[][];
}

export class TreeSelect extends AbstractFormControl<HTMLElement> {

    private dropdown: StimulusDropdown;
    private treeMenuEl: HTMLElement;
    private tree: Tree;
    private separator: string | null;
    private clearBtn: ClearButton;

    private selectedLeafId: string = '';
    private lists: TreeList[] = [];

    initialize(): void {
        this.treeMenuEl = this.element.querySelector<HTMLElement>('.ww-tree-menu');
        this.dropdown = new StimulusDropdown(this.element);
        let data = <TreeSelectData> utils.importJson(this.element);
        this.tree = Tree.initialize(data.branches);
        this.clearBtn = new ClearButton(this.element.querySelector('.ww-tree-clear-btn'));
        let prevBtn = new TreeSelectButton(this.element.querySelector('.ww-tree-previous-btn'));
        let nextBtn = new TreeSelectButton(this.element.querySelector('.ww-tree-next-btn'));
        this.separator = data.separator;

        this.clearBtn.onClick(this.onClearBtnClick);
        prevBtn.onClick(this.onPrevBtnClick);
        nextBtn.onClick(this.onNextBtnClick);
        this.initializeLists();
        this.updateList(0, this.tree.nodes);

        this.setValue(data.value);
    }

    contains(el: HTMLElement): boolean {
        return this.dropdown.contains(el);
    }

    private initializeLists() {
        for (let i = 0; i < this.tree.depth; i++) {
            this.addList(TreeList.create());
        }
    }

    private addList(list: TreeList) {
        this.treeMenuEl.appendChild(list.el);
        this.lists.push(list);
    }

    public getName(): string {
        return this.element.dataset.name;
    }

    public getValue() {
        return this.selectedLeafId;
    }

    public setValue(leafId?: string): void {
        if (this.selectedLeafId == leafId) {
            return;
        }

        this.selectedLeafId = leafId;
        if (!this.selectedLeafId) {
            this.clearSelections();
            return;
        }

        let node = this.tree.getLeaf(leafId);
        if (node) this.getPath(node).forEach((node, index) =>
            this.selectNode(index, node)
        );
    }

    public getPath(node: TreeNode) {
        let path = <TreeNode[]>[];
        for (let index = this.tree.depth - 1; index >= 0; index--) {
            path[index] = node;
            node = node.parent;
        }
        return path;
    }

    public clearSelections() {
        this.updateText(null);
        this.clearBtn.hide();
        this.selectedLeafId = null;
        for (let i = 0; i < this.lists.length; i++) {
            let list = this.lists[i];
            list.setSelectedItemId(null);
            if (i > 0) {
                list.el.innerHTML = '';
            }
        }
    }

    public selectNode(listIndex: number, node: TreeNode) {
        let list = this.lists[listIndex];
        if (list.selectedItemId == node.id) {
            return;
        }

        list.setSelectedItemId(node.id);
        if (listIndex == this.lists.length - 1) {
            this.clearBtn.show();
            this.updateText(node);
        } else {
            this.updateList(listIndex + 1, node.children);
        }
    }

    public updateText(node?: TreeNode) {
        this.dropdown.text = this.getDropdownText(node);
    }

    private getDropdownText(node?: TreeNode) {
        if (!node) return '';
        if (!this.separator) return node.name;
        return this.getPath(node).map(node => node.name).join(this.separator)
    }

    public clearList(listIndex: number) {
        while (listIndex < this.lists.length) {
            this.lists[listIndex++].clear();
        }
    }

    public updateList(listIndex: number, nodes: TreeNode[]) {
        this.clearList(listIndex);
        let list = this.lists[listIndex];
        for (let node of nodes) {
            list.addItem(node).addEventListener('click', () => this.onListItemClick(listIndex, node))
        }
    }

    private onListItemClick(listIndex: number, node: TreeNode) {
        this.selectNode(listIndex, node);
        if (listIndex + 1 == this.tree.depth) {
            this.selectedLeafId = node.id
            this.dropdown.close();
            this.dispatchChangeEvent();
        }
    }

    private onClearBtnClick = () => {
        this.setValue(null);
    }

    private onPrevBtnClick = () => {
        this.navigate(-1);
    }

    private onNextBtnClick = () => {
        this.navigate(1);
    }

    private navigate(direction: number) {
        let node = this.tree.getLeaf(this.selectedLeafId);
        if (node) {
            let next = this.tree.getNeighbor(node, direction);
            if (next) {
                this.setValue(next.id);
                this.dispatchChangeEvent();
            }
        }
    }

    private dispatchChangeEvent() {
        this.element.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: this }));
    }
}

export interface BranchData {
    parent?: string;
    bubbles: boolean;
    blankOption?: string;
    options: TreeNodeData[]
}

export class BranchSelect extends SelectControl {

    private bdata: BranchData;

    initialize(): void {
        let el = this.element;
        let jsonEl = <HTMLScriptElement>el.nextElementSibling;
        this.bdata = <BranchData> utils.importJsonFromScript(jsonEl);
        if (this.bdata.parent) {
            this.initializeAsChild();
        } else {
            this.initializeAsParent();
        }
    }

    private initializeAsChild() {
        let value = this.getValue();
        this.parentEl = this.getParent();
        this.parentEl.addEventListener('change', this.onParentChange);
        this.updateOptions(this.parentEl.value); // Clears initial value + populates dropdown based on parentId
        this.element.value = value; // Restores initial value
    }

    private getParent(): HTMLSelectElement {
        return utils.closestTo(this.element, `.ww-branch-select[name='${this.bdata.parent}']`) as HTMLSelectElement;
    }

    private initializeAsParent() {
        let value = this.getValue();
        this.setOptions(this.bdata.options); // Clears initial value
        this.element.value = value; // Restores initial value
    }

    private parentEl: HTMLSelectElement;

    setValue(value: any): void {
        if (this.parentEl) {
            this.updateOptions(this.getParentId(value))
        }
        this.element.value = value;
    }

    private getParentId(nodeId) {
        for (let [id, name, parentId] of this.bdata.options) {
            if (id == nodeId) {
                return parentId;
            }
        }
        return this.parentEl.value;
    }

    private onParentChange = (e: Event) => {
        this.updateOptions((<HTMLSelectElement>e.target).value);
        if (this.bdata.bubbles) {
            this.element.dispatchEvent(new CustomEvent('change'));
        }
    }

    private updateOptions(parentId?: string) {
        this.setOptions(this.bdata.options.filter(node => node[2] == parentId));
    }

    private setOptions(visibleNodes: TreeNodeData[]) {
        this.element.innerHTML = '';
        if (this.bdata.blankOption !== null) {
            this.addOption('', this.bdata.blankOption, false);
        }
        for (let [id, name] of visibleNodes) {
            this.addOption(id, name, false);
        }
    }
}
